import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const MODAL_ID = 'back-in-stock-modal';

/** How long to wait for the captcha and the post before handing the form back, in ms. */
const STALL_TIMEOUT = 30000;

/** Option values the migrated catalog uses as «no option», hidden by the variant picker too. */
const PLACEHOLDER_OPTION_VALUES = ['', 'sin', '0', 'none', 'default title'];

/**
 * @typedef {object} BackInStockRequest
 * @property {string} productTitle
 * @property {string} productUrl
 * @property {string} variantId
 * @property {string} variantTitle
 * @property {string} sku
 * @property {string} [image]
 */

/**
 * Mirrors the 'variant-label' snippet: drops the placeholder option values.
 *
 * @param {string[] | undefined} options
 * @returns {string}
 */
function variantLabel(options) {
  if (!options) return '';

  return options
    .map((option) => option?.trim() ?? '')
    .filter((option) => !PLACEHOLDER_OPTION_VALUES.includes(option.toLowerCase()))
    .join(' / ');
}

/**
 * Sold-out CTA. Tracks the selected variant and opens the back-in-stock modal with it.
 *
 * @extends {Component}
 */
class BackInStockTrigger extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.closest('.shopify-section, dialog, product-card')?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  openModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!(modal instanceof HTMLElement)) return;

    const form = modal.querySelector('back-in-stock-form');
    if (form instanceof BackInStockForm) form.prefill(this.#request);

    if ('showDialog' in modal && typeof modal.showDialog === 'function') modal.showDialog();
  }

  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // No variant matches the selected options, so there is nothing to be notified about.
    if (!variant) {
      this.hidden = true;
      return;
    }

    this.dataset.variantId = variant.id ?? '';
    this.dataset.variantTitle = variantLabel(variant.options);
    this.dataset.variantSku = variant.sku ?? '';

    const image = variant.featured_image?.src ?? variant.featured_media?.preview_image?.src;
    if (image) this.dataset.variantImage = image;

    this.hidden = variant.available === true;
  };

  /** @returns {BackInStockRequest} */
  get #request() {
    const { productTitle, productUrl, variantId, variantTitle, variantSku, variantImage } = this.dataset;
    const url = variantId ? `${productUrl}?variant=${variantId}` : productUrl ?? '';

    return {
      productTitle: productTitle ?? '',
      productUrl: url,
      variantId: variantId ?? '',
      variantTitle: variantTitle ?? '',
      sku: variantSku ?? '',
      image: variantImage,
    };
  }
}

/**
 * Back-in-stock request form.
 *
 * The form is submitted natively: the storefront runs Shopify's captcha protection on `submit`
 * and rejects programmatic posts, so this only fills the request details, then reopens the modal
 * on the result Shopify rendered after redirecting back to the product.
 *
 * @typedef {object} BackInStockFormRefs
 * @property {HTMLElement} fields
 * @property {HTMLElement} success
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} submit
 * @property {HTMLInputElement} fieldProduct
 * @property {HTMLInputElement} fieldVariant
 * @property {HTMLInputElement} fieldSku
 * @property {HTMLInputElement} fieldUrl
 * @property {HTMLInputElement} fieldBody
 * @property {HTMLElement} [product]
 * @property {HTMLElement} [productTitle]
 * @property {HTMLElement} [variantTitle]
 * @property {HTMLImageElement} [image]
 *
 * @extends {Component<BackInStockFormRefs>}
 */
class BackInStockForm extends Component {
  requiredRefs = [
    'fields',
    'success',
    'error',
    'submit',
    'fieldProduct',
    'fieldVariant',
    'fieldSku',
    'fieldUrl',
    'fieldBody',
  ];

  connectedCallback() {
    super.connectedCallback();
    this.#restoreSubmissionResult();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#stallTimeout);
  }

  /**
   * Fills the modal with the product and variant the shopper asked about.
   *
   * @param {BackInStockRequest} request
   */
  prefill(request) {
    const { product, productTitle, variantTitle, image, fields, success, error } = this.refs;

    if (product) product.hidden = false;
    if (productTitle) productTitle.textContent = request.productTitle;

    if (variantTitle) {
      variantTitle.textContent = request.variantTitle;
      variantTitle.hidden = request.variantTitle === '';
    }

    if (image && request.image) image.src = request.image;

    this.refs.fieldProduct.value = request.productTitle;
    this.refs.fieldVariant.value = request.variantTitle;
    this.refs.fieldSku.value = request.sku;
    this.refs.fieldUrl.value = request.productUrl;
    this.refs.fieldBody.value = this.#buildBody(request);

    fields.hidden = false;
    success.hidden = true;
    error.hidden = true;
    this.#setDialogLabel('back-in-stock-title');
  }

  /**
   * Marks the button as busy while Shopify solves its captcha and posts the form.
   */
  handleSubmit() {
    const { submit } = this.refs;
    const label = submit.dataset.labelSending;

    submit.setAttribute('aria-busy', 'true');
    if (label) submit.textContent = label;

    // The captcha never resolves on some connections, and the page would then sit on a button that
    // spins forever, so give the shopper the form back instead of trapping them.
    clearTimeout(this.#stallTimeout);
    this.#stallTimeout = setTimeout(() => this.#releaseSubmit(), STALL_TIMEOUT);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #stallTimeout;

  /**
   * Restores the button so a stalled captcha can be retried.
   */
  #releaseSubmit() {
    const { submit, error } = this.refs;
    const label = submit.dataset.label;

    submit.removeAttribute('aria-busy');
    if (label) submit.textContent = label;
    error.hidden = false;
  }

  /**
   * Reopens the modal on the confirmation (or error) the storefront rendered after submitting.
   */
  #restoreSubmissionResult() {
    const url = new URL(window.location.href);
    // Liquid only reveals these panels for a submission of this form, never a sibling contact form.
    const posted = !this.refs.success.hidden || this.#wasPostedFromUrl(url);
    const failed = !this.refs.error.hidden;

    if (!posted && !failed) return;

    if (posted) {
      this.refs.fields.hidden = true;
      this.refs.success.hidden = false;
      // The redirect drops the selected variant, so don't claim which one was requested.
      if (this.refs.product) this.refs.product.hidden = true;
      this.#setDialogLabel('back-in-stock-success-title');
    }

    // A rejected submission also comes back without the variant, so the page falls back to its
    // default one. Rebuild the request from the fields Shopify echoes into the URL, otherwise
    // retrying would ask about a variant the shopper never picked.
    const request = failed ? this.#requestFromUrl(url) : null;

    if (request) {
      this.prefill(request);
      this.refs.error.hidden = false;
    }

    const modal = this.closest('dialog-component');
    if (modal && 'showDialog' in modal && typeof modal.showDialog === 'function') modal.showDialog();

    this.#cleanUrl(url);
  }

  /**
   * Reads the request Shopify echoes back into the query string when it rejects a submission.
   *
   * @param {URL} url
   * @returns {BackInStockRequest | null}
   */
  #requestFromUrl(url) {
    const productTitle = url.searchParams.get('contact[Producto]');

    if (!productTitle) return null;

    return {
      productTitle,
      productUrl: url.searchParams.get('contact[Enlace]') ?? '',
      variantId: '',
      variantTitle: url.searchParams.get('contact[Variante]') ?? '',
      sku: url.searchParams.get('contact[SKU]') ?? '',
    };
  }

  /**
   * Drops the submission bookkeeping Shopify appends, so the shopper keeps a shareable URL.
   *
   * @param {URL} url
   */
  #cleanUrl(url) {
    const keys = [...url.searchParams.keys()].filter(
      (key) => key === 'contact_posted' || key === 'form_type' || key.startsWith('contact[')
    );

    if (!keys.length) return;

    for (const key of keys) url.searchParams.delete(key);

    history.replaceState({}, '', url.toString());
  }

  /**
   * Shopify carries the action fragment («/contact#BackInStockForm») over to the redirect, so it
   * tells this form's submission apart from the other contact forms on the page.
   *
   * @param {URL} url
   * @returns {boolean}
   */
  #wasPostedFromUrl(url) {
    const formId = this.querySelector('form')?.id;

    return Boolean(formId) && url.searchParams.get('contact_posted') === 'true' && url.hash === `#${formId}`;
  }

  /**
   * Points the dialog label at the visible heading for the current state.
   *
   * @param {string} id
   */
  #setDialogLabel(id) {
    const dialog = this.closest('dialog');
    if (dialog) dialog.setAttribute('aria-labelledby', id);
  }

  /**
   * Builds the message body so the request is readable in the notification email.
   *
   * @param {BackInStockRequest} request
   * @returns {string}
   */
  #buildBody(request) {
    const { subject, labelProduct, labelVariant, labelLink } = this.refs.fieldBody.dataset;
    const lines = [subject ?? '', '', `${labelProduct}: ${request.productTitle}`];

    if (request.variantTitle) lines.push(`${labelVariant}: ${request.variantTitle}`);
    if (request.sku) lines.push(`SKU: ${request.sku}`);
    if (request.productUrl) lines.push(`${labelLink}: ${request.productUrl}`);

    return lines.join('\n').trim();
  }
}

if (!customElements.get('back-in-stock-trigger')) {
  customElements.define('back-in-stock-trigger', BackInStockTrigger);
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
